import { Button, Form, Input, Select, Typography } from 'antd';
import type { FormInstance } from 'antd';
import type { CreateCustomerBody, CustomerDocumentType } from '@goelectric/shared-types';
import { CUSTOMER_DOCUMENT_TYPE_LABELS } from '../utils/format';

/** Estado del formulario de alta (campos del contrato `CustomerCreateRequest`). */
export interface CreateCustomerFormState {
  fullName: string;
  phone: string;
  email?: string;
  documentType?: CustomerDocumentType;
  documentNumber?: string;
}

const DOCUMENT_TYPE_OPTIONS = (['CC', 'CE', 'NIT', 'PPT'] as const).map((value) => ({
  value,
  label: CUSTOMER_DOCUMENT_TYPE_LABELS[value],
}));

/**
 * Compone el cuerpo del contrato; `status` arranca en `ACTIVE` (alta desde el Admin).
 * Los campos opcionales vacíos se omiten (no viajan `undefined` ni `''`). El alta ya no
 * envía aliado: la relación cliente↔aliado se deriva de la actividad (ADR 0049).
 */
export function buildCreateCustomerPayload(values: CreateCustomerFormState): CreateCustomerBody {
  const body: CreateCustomerBody = {
    fullName: values.fullName.trim(),
    phone: values.phone.trim(),
    status: 'ACTIVE',
  };
  const email = values.email?.trim();
  if (email) body.email = email;
  if (values.documentType) body.documentType = values.documentType;
  const documentNumber = values.documentNumber?.trim();
  if (documentNumber) body.documentNumber = documentNumber;
  return body;
}

export interface CustomerFormProps {
  /** Recibe el cuerpo del contrato ya compuesto; la llamada a la API la hace la página. */
  onSubmit: (body: CreateCustomerBody) => void;
  submitting?: boolean;
  /** Instancia del `Form` para que la página marque campos con errores del backend. */
  form?: FormInstance<CreateCustomerFormState>;
  /** Texto del botón de envío. */
  submitLabel?: string;
}

/**
 * Formulario de alta de cliente (US-2.3): datos de contacto + documento (opcional).
 * La etiqueta siempre es visible y la validación es inline (guía de estilo §8). El
 * aliado no se elige en el alta: la relación se deriva de la actividad (ADR 0049).
 */
export function CustomerForm({
  onSubmit,
  submitting = false,
  form: formProp,
  submitLabel = 'Crear cliente',
}: CustomerFormProps) {
  const [internalForm] = Form.useForm<CreateCustomerFormState>();
  const form = formProp ?? internalForm;

  const handleFinish = (values: CreateCustomerFormState) => {
    onSubmit(buildCreateCustomerPayload(values));
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} autoComplete="off">
      <Form.Item
        label="Nombre completo"
        name="fullName"
        rules={[{ required: true, message: 'Ingresa el nombre del cliente.' }]}
      >
        <Input placeholder="Introduzca el nombre del cliente" />
      </Form.Item>

      <Form.Item
        label="Teléfono"
        name="phone"
        rules={[{ required: true, message: 'Ingresa el teléfono del cliente.' }]}
      >
        <Input placeholder="Introduzca el teléfono del cliente" />
      </Form.Item>

      <Form.Item
        label="Email"
        name="email"
        rules={[{ type: 'email', message: 'Ingresa un email válido.' }]}
      >
        <Input placeholder="Introduzca el email del cliente" />
      </Form.Item>

      <Form.Item label="Tipo de documento" name="documentType">
        <Select allowClear placeholder="Selecciona el tipo de documento" options={DOCUMENT_TYPE_OPTIONS} />
      </Form.Item>

      <Form.Item label="Número de documento" name="documentNumber">
        <Input placeholder="Introduzca el número de documento" />
      </Form.Item>

      <Typography.Text type="secondary">
        El alta crea la cuenta en estado activo. El saldo se ajusta después desde el detalle.
      </Typography.Text>

      <Form.Item style={{ marginTop: 16, marginBottom: 0 }}>
        <Button type="primary" htmlType="submit" loading={submitting}>
          {submitLabel}
        </Button>
      </Form.Item>
    </Form>
  );
}
